import { createContext, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { initMeedioClient } from 'api/meedioBackend/client';
import { setIsTokenGetter } from 'api/meedioBackend/isTokenBridge';
import { registerIsToken } from 'api/meedioBackend/registerIsToken';
import { RegisterRequest } from 'api/meedioBackend/types';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { MATRIX_TOKEN_REFRESHED_EVENT } from 'contexts/MatrixContext/useProactiveMatrixTokenRefresh';
import logger from 'utils/logging/faro';

export interface IsTokenContextType {
  getToken: () => Promise<string>;
  refreshToken: () => Promise<string>;
}

export const IsTokenContext = createContext<IsTokenContextType | undefined>(undefined);

const IsTokenProvider = ({ children }: PropsWithChildren) => {
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const [, setToken] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const refreshPromiseRef = useRef<Promise<string> | null>(null);

  const exchangeToken = useCallback(async (): Promise<string> => {
    if (!matrixClient) throw new Error('Matrix client not available for IS token exchange');

    const openIdToken = await matrixClient.getOpenIdToken();
    const payload: RegisterRequest = {
      token_type: 'Bearer',
      matrix_server_name: openIdToken.matrix_server_name,
      access_token: openIdToken.access_token,
      expires_in: openIdToken.expires_in,
    };

    const { token } = await registerIsToken(payload);

    tokenRef.current = token;
    setToken(token);
    return token;
  }, [matrixClient]);

  const refreshToken = useCallback(async (): Promise<string> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const promise = exchangeToken().finally(() => {
      refreshPromiseRef.current = null;
    });

    refreshPromiseRef.current = promise;
    return promise;
  }, [exchangeToken]);

  const getToken = useCallback(async (): Promise<string> => {
    if (tokenRef.current) return tokenRef.current;
    return refreshToken();
  }, [refreshToken]);

  useEffect(() => {
    initMeedioClient(getToken, refreshToken);
    setIsTokenGetter(getToken);
  }, [getToken, refreshToken]);

  useEffect(() => {
    if (!matrixClient) return;

    refreshToken().catch((error) => {
      logger.error('Failed to exchange IS token on mount', { error });
    });
  }, [matrixClient, refreshToken]);

  useEffect(() => {
    const onMatrixTokenRefreshed = () => {
      refreshToken().catch((error) => {
        logger.error('Failed to exchange IS token after Matrix refresh', { error });
      });
    };

    window.addEventListener(MATRIX_TOKEN_REFRESHED_EVENT, onMatrixTokenRefreshed);
    return () => {
      window.removeEventListener(MATRIX_TOKEN_REFRESHED_EVENT, onMatrixTokenRefreshed);
    };
  }, [refreshToken]);

  return <IsTokenContext.Provider value={{ getToken, refreshToken }}>{children}</IsTokenContext.Provider>;
};

export default IsTokenProvider;
